import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { AvatarStage } from "@/components/AvatarStage";
import { CertificateDialog } from "@/components/Certificate";
import { LanguageSelect, useLanguage } from "@/components/LanguageSelect";
import { ArrowLeft, Play, Pause, SkipForward, SkipBack, Loader2, CheckCircle2, Swords, ArrowRight, Video, Sparkles, RotateCcw, Award } from "lucide-react";

const COACH_AVATAR = "https://static.prod-images.emergentagent.com/jobs/1a9e1561-ca18-4dbb-8f52-13e836a03554/images/063325646653cf8a151ee440b3e604a7efe09ddef4648003466f156b1d0b378f.jpeg";

function embedUrl(url) {
  try {
    const u = new URL(url);
    const host = u.hostname.replace("www.", "");
    if (host === "youtu.be") return `https://www.youtube.com/embed/${u.pathname.slice(1)}`;
    if (host.endsWith("youtube.com")) {
      const id = u.searchParams.get("v") || u.pathname.split("/").filter(Boolean).pop();
      return `https://www.youtube.com/embed/${id}`;
    }
    if (host.endsWith("vimeo.com")) return `https://player.vimeo.com/video/${u.pathname.split("/").filter(Boolean).pop()}`;
    if (host.endsWith("loom.com")) return url.replace("/share/", "/embed/");
  } catch (err) { console.warn("[Lesson] ignored error", err); }
  return null;
}

export function VideoEmbed({ url }) {
  const embed = embedUrl(url);
  return (
    <div className="rounded-2xl overflow-hidden bg-slate-900 aspect-video shadow-lg" data-testid="lesson-video-embed">
      {embed
        ? <iframe src={embed} title="Lesson video" className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
        : <video src={url} controls className="w-full h-full" />}
    </div>
  );
}

function useNarrator(voice, language) {
  const [speaking, setSpeaking] = useState(false);
  const [level, setLevel] = useState(0);
  const audioRef = useRef(null);
  const ctxRef = useRef(null);
  const rafRef = useRef(null);
  const cache = useRef({});

  const stop = () => {
    if (audioRef.current) { audioRef.current.onended = null; audioRef.current.pause(); audioRef.current = null; }
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    setSpeaking(false); setLevel(0);
  };
  useEffect(() => () => { stop(); ctxRef.current?.close?.(); }, []); // eslint-disable-line

  const meter = (a) => {
    try {
      const ctx = ctxRef.current || new (window.AudioContext || window.webkitAudioContext)();
      ctxRef.current = ctx; ctx.resume?.();
      const src = ctx.createMediaElementSource(a);
      const an = ctx.createAnalyser(); an.fftSize = 256;
      src.connect(an); an.connect(ctx.destination);
      const buf = new Uint8Array(an.frequencyBinCount);
      let frame = 0;
      const tick = () => {
        if (a.paused || a.ended) { setLevel(0); return; }
        an.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v; }
        if (frame++ % 2 === 0) setLevel(Math.min(1, Math.sqrt(sum / buf.length) * 3.5));
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch (err) { console.warn("[Lesson] ignored error", err); }
  };

  const fetchAudio = async (key, text) => {
    if (cache.current[key]) return cache.current[key];
    const { data } = await api.post("/voice/tts", { text, voice: voice?.voice || "coach", speed: voice?.speed || 1.0, language });
    cache.current[key] = data.audio_base64;
    return data.audio_base64;
  };

  const speak = (key, text) => new Promise(async (resolve) => {
    try {
      stop();
      const b64 = await fetchAudio(key, text);
      if (!b64) return resolve(false);
      const a = new Audio(`data:audio/mp3;base64,${b64}`);
      audioRef.current = a;
      a.onended = () => { setSpeaking(false); setLevel(0); resolve(true); };
      a.onerror = () => { setSpeaking(false); setLevel(0); resolve(false); };
      setSpeaking(true);
      meter(a);
      a.play().catch(() => { setSpeaking(false); resolve(false); });
    } catch { setSpeaking(false); resolve(false); }
  });

  useEffect(() => () => { stop(); try { ctxRef.current?.close(); } catch (err) { console.warn("[Lesson] ignored error", err); } }, []); // eslint-disable-line
  return { speak, stop, speaking, level, prefetch: fetchAudio };
}

function Quiz({ quiz }) {
  const [picked, setPicked] = useState(null);
  if (!quiz?.options?.length) return null;
  return (
    <div className="rounded-3xl bg-white border border-slate-200/80 shadow-[0_4px_20px_-4px_rgba(15,23,42,0.06)] p-5" data-testid="lesson-quiz">
      <div className="text-xs font-bold uppercase tracking-widest text-emerald-700">Quick check</div>
      <p className="font-semibold text-slate-900 mt-1">{quiz.question}</p>
      <div className="mt-3 grid gap-2">
        {quiz.options.map((o, i) => {
          const correct = picked !== null && i === quiz.answer;
          const wrong = picked === i && i !== quiz.answer;
          return (
            <button key={`${i}-${o}`} onClick={() => setPicked(i)} disabled={picked !== null}
              className={`text-left px-4 py-3 rounded-xl border text-sm transition-colors ${correct ? "border-emerald-500 bg-emerald-500/10 text-emerald-800" : wrong ? "border-red-400 bg-red-50 text-red-700" : "border-slate-200 bg-white hover:border-emerald-400 text-slate-700"}`}
              data-testid={`quiz-option-${i}`}>{o}</button>
          );
        })}
      </div>
      {picked !== null && <p className="text-sm text-slate-600 mt-3" data-testid="quiz-explanation">{picked === quiz.answer ? "Spot on. " : "Not quite. "}{quiz.why}</p>}
    </div>
  );
}

export default function Lesson() {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [coachFrames, setCoachFrames] = useState([]);
  const [idx, setIdx] = useState(-1); // -1 hook, 0..n-1 segments, n closing
  const [playing, setPlaying] = useState(false);
  const [finished, setFinished] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [cert, setCert] = useState(null);
  const [showCert, setShowCert] = useState(false);
  const playingRef = useRef(false);
  const lang = useLanguage();
  const narrator = useNarrator(data?.coach_voice, lang.language);

  useEffect(() => {
    setData(null); setError(null); setIdx(-1); setFinished(false); setPlaying(false); playingRef.current = false; narrator.stop();
    api.get(`/courses/lesson/${lessonId}/video`, { params: lang.language !== "en" ? { lang: lang.language } : {} })
      .then((r) => { setData(r.data); const s = r.data?.script; if (s?.hook) narrator.prefetch(`${lessonId}-${lang.language}-hook`, s.hook).catch(() => {}); })
      .catch((e) => setError(e?.response?.data?.detail || "Couldn't load this lesson."));
  }, [lessonId, lang.language]); // eslint-disable-line
  useEffect(() => () => { playingRef.current = false; }, []);
  useEffect(() => { api.get("/avatars/coach").then((r) => setCoachFrames(r.data.frames || [])).catch((err) => console.warn("[Lesson] request failed", err)); }, []);

  const script = data?.script;
  const steps = script ? [
    { key: "hook", heading: data.lesson.title, text: script.hook, points: [] },
    ...script.segments.map((s, i) => ({ key: `s${i}`, heading: s.heading, text: s.narration, points: s.key_points || [] })),
    { key: "closing", heading: "Your move", text: script.closing, points: [] },
  ] : [];
  const cur = steps[Math.max(0, idx + 1)] || steps[0];
  const pos = idx + 1;

  const runFrom = async (i) => {
    playingRef.current = true;
    setPlaying(true);
    for (let k = i; k < steps.length; k++) {
      if (!playingRef.current) return;
      setIdx(k - 1);
      if (steps[k + 1]) narrator.prefetch(`${lessonId}-${lang.language}-${steps[k + 1].key}`, steps[k + 1].text).catch((err) => console.warn("[Lesson] request failed", err));
      const ok = await narrator.speak(`${lessonId}-${lang.language}-${steps[k].key}`, steps[k].text);
      if (!ok && playingRef.current) { toast.error("Playback stopped — tap play to resume."); playingRef.current = false; setPlaying(false); return; }
      if (!playingRef.current) return;
    }
    playingRef.current = false;
    setPlaying(false);
    setFinished(true);
  };

  const pause = () => { playingRef.current = false; narrator.stop(); setPlaying(false); };
  const seek = (delta) => {
    const next = Math.min(steps.length - 1, Math.max(0, pos + delta));
    if (playing) { pause(); runFrom(next); } else setIdx(next - 1);
  };

  const complete = async () => {
    setCompleting(true);
    try {
      const { data: res } = await api.post(`/courses/lesson/${lessonId}/complete`);
      refreshUser();
      setData((d) => ({ ...d, lesson: { ...d.lesson, completed: true } }));
      if (res.certificate) {
        setCert(res.certificate); setShowCert(true);
        toast.success(`Course complete! Your ${res.certificate.course_title} certificate is ready.`, { icon: "🏅" });
      } else toast.success("Lesson complete · +100 XP");
    } catch { toast.error("Couldn't mark complete."); } finally { setCompleting(false); }
  };

  if (error) return (
    <div className="py-24 text-center" data-testid="lesson-error">
      <p className="text-slate-700 font-medium">{error}</p>
      <Button variant="outline" onClick={() => navigate("/app/courses")} className="mt-4"><ArrowLeft className="w-4 h-4 mr-1.5" /> Back to courses</Button>
    </div>
  );
  if (!data) return <div className="flex items-center justify-center py-32 text-emerald-600" data-testid="lesson-loading"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Linden is preparing your lesson{lang.current && lang.current.id !== "en" ? ` in ${lang.current.label}` : ""}…</div>;

  const avatarState = narrator.speaking ? "speaking" : playing ? "thinking" : "idle";
  const progress = steps.length ? Math.round(((finished ? steps.length : pos) / steps.length) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="lesson-page">
      <div className="relative overflow-hidden rounded-3xl bg-white border border-slate-200/80 shadow-[0_4px_20px_-4px_rgba(15,23,42,0.06)] p-5 sm:p-6">
        <div className="absolute -top-24 -right-20 w-72 h-72 rounded-full blur-3xl pointer-events-none" style={{ background: `${data.course.color || "#10B981"}33` }} />
        <div className="relative flex items-center gap-3 flex-wrap">
          <Button variant="ghost" size="icon" onClick={() => { pause(); navigate("/app/courses"); }} className="text-slate-500 hover:text-slate-900 -ml-2" data-testid="lesson-back-btn"><ArrowLeft className="w-5 h-5" /></Button>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center font-display font-extrabold text-black shadow-sm shrink-0" style={{ background: data.course.color }}>{data.course.level}</div>
          <div className="min-w-0 flex-1 basis-[60%]">
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500 truncate">{data.course.title} · {data.lesson.duration}</p>
            <h1 className="text-xl sm:text-3xl font-extrabold tracking-tight text-slate-900 leading-tight" data-testid="lesson-title">{data.lesson.title}</h1>
          </div>
          <div className="basis-full sm:basis-auto sm:ml-auto flex items-center justify-end gap-2 shrink-0">
            {data.lesson.completed && <span className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-800 bg-emerald-100/80 border border-emerald-200/80 rounded-full px-3 py-1" data-testid="lesson-completed-badge"><CheckCircle2 className="w-3.5 h-3.5" /> Completed</span>}
            <LanguageSelect {...lang} testId="lesson-language-select" title="Lesson language" />
          </div>
        </div>
      </div>

      {data.video_url && (
        <div>
          <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-cyan-700 mb-2"><Video className="w-3.5 h-3.5" /> Lesson video</div>
          <VideoEmbed url={data.video_url} />
        </div>
      )}

      {script ? (
        <div className="grid lg:grid-cols-[320px_1fr] gap-5">
          <AvatarStage name="Linden" title={data.video_url ? "AI recap" : "Your coach on camera"} avatar={COACH_AVATAR} frameKey="coach" frames={coachFrames}
            state={avatarState} level={narrator.speaking ? narrator.level : undefined}
            subtitle={playing ? `Part ${Math.min(pos + 1, steps.length)} of ${steps.length}` : finished ? "Lesson finished" : "Press play to start"} />

          <div className="rounded-3xl bg-white border border-slate-200/80 shadow-[0_4px_20px_-4px_rgba(15,23,42,0.06)] p-5 sm:p-7 flex flex-col bg-[radial-gradient(ellipse_at_top_right,rgba(16,185,129,0.07),transparent_55%)]" data-testid="lesson-player">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-emerald-700"><Sparkles className="w-3.5 h-3.5" /> {data.video_url ? "AI video recap" : "AI video lesson"}</div>
            <AnimatePresence mode="wait">
              <motion.div key={cur?.key} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.25 }} className="mt-3 flex-1">
                <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900 tracking-tight" data-testid="lesson-segment-heading">{cur?.heading}</h2>
                <p className="text-slate-600 mt-3 leading-relaxed" data-testid="lesson-segment-text">{cur?.text}</p>
                {cur?.points?.length > 0 && (
                  <ul className="mt-5 space-y-2">
                    {cur.points.map((p, i) => (
                      <motion.li key={p} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.35 + i * 0.25 }}
                        className="flex items-start gap-2.5 text-sm text-slate-800 bg-emerald-500/5 border border-emerald-500/20 rounded-xl px-3.5 py-2.5">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> {p}
                      </motion.li>
                    ))}
                  </ul>
                )}
              </motion.div>
            </AnimatePresence>

            <div className="mt-6">
              <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-emerald-500 transition-all duration-500" style={{ width: `${progress}%` }} /></div>
              <div className="mt-4 flex items-center gap-2 flex-wrap">
                <Button variant="outline" size="icon" onClick={() => seek(-1)} disabled={pos <= 0} className="rounded-full h-11 w-11" data-testid="lesson-prev-btn"><SkipBack className="w-4 h-4" /></Button>
                {playing
                  ? <Button onClick={pause} className="rounded-full h-12 px-6 bg-slate-900 hover:bg-slate-800 text-white font-semibold shadow-md" data-testid="lesson-pause-btn"><Pause className="w-5 h-5 mr-2" /> Pause</Button>
                  : <Button onClick={() => { if (finished) { setFinished(false); runFrom(0); } else runFrom(Math.max(0, pos)); }} className="rounded-full h-12 px-6 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold shadow-[0_4px_14px_rgba(16,185,129,0.35)] hover:-translate-y-0.5 transition-all" data-testid="lesson-play-btn">
                      {finished ? <><RotateCcw className="w-5 h-5 mr-2" /> Replay</> : <><Play className="w-5 h-5 mr-2" /> {pos > 0 ? "Resume" : "Play lesson"}</>}
                    </Button>}
                <Button variant="outline" size="icon" onClick={() => seek(1)} disabled={pos >= steps.length - 1} className="rounded-full h-11 w-11" data-testid="lesson-next-btn"><SkipForward className="w-4 h-4" /></Button>
                <div className="ml-auto flex gap-1.5" data-testid="lesson-segment-dots">
                  {steps.map((s, i) => <button key={s.key} onClick={() => { if (playing) { pause(); runFrom(i); } else setIdx(i - 1); }} className={`w-2.5 h-2.5 rounded-full transition-colors ${i === pos ? "bg-emerald-500" : i < pos || finished ? "bg-emerald-300" : "bg-slate-200"}`} aria-label={`Part ${i + 1}`} />)}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : !data.video_url && (
        <div className="rounded-3xl bg-white border border-slate-200/80 p-6 text-center text-slate-600" data-testid="lesson-no-script">Linden couldn't record this lesson just now. <button onClick={() => window.location.reload()} className="text-emerald-700 underline">Try again</button>.</div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <Quiz quiz={script?.quiz} />
        <div className="rounded-3xl bg-white border border-slate-200/80 shadow-[0_4px_20px_-4px_rgba(15,23,42,0.06)] p-5 flex flex-col bg-gradient-to-br from-white via-white to-emerald-50/60" data-testid="lesson-actions">
          <div className="text-xs font-bold uppercase tracking-widest text-emerald-700">Put it into practice</div>
          {data.practice_persona && (
            <button onClick={() => navigate(`/app/roleplay?persona=${data.practice_persona.id}`)} className="mt-3 flex items-center gap-3 text-left group" data-testid="lesson-practise-btn">
              <img src={data.practice_persona.avatar} alt="" className="w-12 h-12 rounded-full object-cover object-top border border-slate-200" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900 group-hover:text-emerald-700 flex items-center gap-1.5"><Swords className="w-4 h-4" /> Practise this now</div>
                <div className="text-xs text-slate-500">Roleplay against {data.practice_persona.title} ({data.practice_persona.name})</div>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-emerald-600 group-hover:translate-x-1 transition-all" />
            </button>
          )}
          <div className="mt-auto pt-4 flex flex-wrap gap-2">
            {!data.lesson.completed && (
              <Button onClick={complete} disabled={completing} className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold h-11 rounded-xl shadow-[0_4px_14px_rgba(16,185,129,0.35)]" data-testid="lesson-complete-btn">
                {completing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />} Mark complete · +100 XP
              </Button>
            )}
            {data.next_lesson && data.lesson.completed && (
              <Button variant="outline" onClick={() => navigate(`/app/courses/${data.next_lesson.id}`)} className="h-11" data-testid="lesson-next-lesson-btn">Next: {data.next_lesson.title} <ArrowRight className="w-4 h-4 ml-1.5" /></Button>
            )}
            {cert && (
              <Button onClick={() => setShowCert(true)} className="h-11 bg-amber-500 hover:bg-amber-600 text-black font-semibold" data-testid="lesson-view-certificate-btn"><Award className="w-4 h-4 mr-2" /> View certificate</Button>
            )}
          </div>
        </div>
      </div>
      <CertificateDialog cert={cert} open={showCert} onClose={() => setShowCert(false)} />
    </div>
  );
}
